import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  InputAdornment, MenuItem, TextField, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  COR, MESES, STATUS, STATUS_ORDEM, abrirNotaFiscal, calcularStatusAutomatico, diasAte,
  formatarTamanho, hojeISO, textoPrazo,
} from './financeiroUtils';
import StatusChip from './StatusChip';

const EXTENSOES = ['.pdf', '.xml', '.png', '.jpg', '.jpeg'];
const TAMANHO_MAXIMO = 10 * 1024 * 1024;

const formVazio = () => {
  const hoje = new Date();
  return {
    mes: hoje.getMonth() + 1, ano: hoje.getFullYear(), projeto: '',
    numero_nf: '', valor: '', numero_parcela: '', total_parcelas: '',
    data_vencimento: '', data_recebimento: '', valor_liquido_recebido: '',
    status_manual: '', observacao: '',
  };
};

const formDaReceita = (r) => ({
  mes: r.mes, ano: r.ano, projeto: r.projeto ?? '',
  numero_nf: r.numero_nf || '', valor: r.valor ?? '',
  numero_parcela: r.numero_parcela ?? '', total_parcelas: r.total_parcelas ?? '',
  data_vencimento: r.data_vencimento || '', data_recebimento: r.data_recebimento || '',
  valor_liquido_recebido: r.valor_liquido_recebido ?? '',
  status_manual: r.status_manual || '', observacao: r.observacao || '',
});

function Secao({ titulo, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COR.ink, mb: 1.5, pb: 0.75, borderBottom: `1px solid ${COR.borda}` }}>
        {titulo}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 2 }}>
        {children}
      </Box>
    </Box>
  );
}

export default function FormularioNF({ aberto, receita, projeto, onFechar, onSalvar }) {
  const [form, setForm] = useState(formVazio());
  const [arquivo, setArquivo] = useState(null);
  const [removerArquivo, setRemoverArquivo] = useState(false);
  const [erros, setErros] = useState({});
  const [mensagem, setMensagem] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputArquivo = useRef(null);

  // Cada vez que o formulário abre, começa do zero (ou com os dados da NF editada).
  useEffect(() => {
    if (!aberto) return;
    setForm(receita ? formDaReceita(receita) : formVazio());
    setArquivo(null);
    setRemoverArquivo(false);
    setErros({});
    setMensagem('');
  }, [aberto, receita]);

  const editando = Boolean(receita);
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const campo = (nome, extras = {}) => ({
    value: form[nome], onChange: mudar(nome), error: Boolean(erros[nome]), helperText: erros[nome] || extras.ajuda,
    fullWidth: true, size: 'small',
  });

  // ------------------------------------------------------------ semáforo ao vivo
  const automatico = calcularStatusAutomatico(form);
  const efetivo = form.status_manual || automatico;
  const prazo = textoPrazo({
    status: efetivo, dias: diasAte(form.data_vencimento), data_recebimento: form.data_recebimento,
  });

  // ------------------------------------------------------------------- arquivo
  const escolherArquivo = (lista) => {
    const escolhido = lista?.[0];
    if (!escolhido) return;
    const nome = escolhido.name.toLowerCase();
    if (!EXTENSOES.some((ext) => nome.endsWith(ext))) {
      setErros((e) => ({ ...e, arquivo_nf: 'Envie a NF em PDF, XML, JPG ou PNG.' }));
      return;
    }
    if (escolhido.size > TAMANHO_MAXIMO) {
      setErros((e) => ({ ...e, arquivo_nf: 'O arquivo pode ter no máximo 10 MB.' }));
      return;
    }
    setErros((e) => ({ ...e, arquivo_nf: '' }));
    setArquivo(escolhido);
    setRemoverArquivo(false);
  };

  const temArquivoAtual = editando && receita.tem_arquivo_nf && !removerArquivo && !arquivo;

  // -------------------------------------------------------------------- salvar
  const salvar = async () => {
    const novos = {};
    if (!form.valor || Number(form.valor) <= 0) novos.valor = 'Informe o valor da NF.';
    if ((form.data_recebimento || efetivo === 'recebido') && form.valor_liquido_recebido === '') novos.valor_liquido_recebido = 'Nota recebida: informe o valor líquido.';
    if (form.numero_parcela && form.total_parcelas && Number(form.numero_parcela) > Number(form.total_parcelas)) {
      novos.numero_parcela = 'A parcela não pode ser maior que o total.';
    }
    if (Object.keys(novos).length) {
      setErros(novos);
      setMensagem('Corrija os campos destacados.');
      return;
    }
    setErros({});
    setMensagem('');
    setSalvando(true);
    try {
      await onSalvar({ form, arquivo, remover: removerArquivo });
    } catch (e) {
      setErros(e.campos || {});
      setMensagem(e.mensagem || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="md" fullWidth scroll="paper" slotProps={{ paper: { sx: { '& .MuiButton-root': { textTransform: 'none', fontWeight: 600 } } } }}>
      <DialogTitle sx={{ pr: 7, pb: 0.5 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 20, fontWeight: 700, color: COR.ink }}>
          {editando ? `Editar NF${receita.numero_nf ? ` ${receita.numero_nf}` : ''}` : 'Lançar NF'}
        </Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 400, color: COR.texto2 }}>
          {projeto?.eh_contrato ? 'Contrato ' : ''}{projeto?.nome}{projeto?.cliente_nome ? `, ${projeto.cliente_nome}` : ''}
        </Typography>
        <IconButton aria-label="Fechar" onClick={onFechar} disabled={salvando} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2.5 }}>
        {mensagem && <Alert severity="error" sx={{ mb: 2 }}>{mensagem}</Alert>}

        {projeto?.eh_contrato && (
          <Secao titulo="De quem é esta NF">
            <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 6' } }}>
              <TextField
                {...campo('projeto', { ajuda: 'Escolha “Contrato inteiro” quando a NF cobre todos os projetos. Se a NF é de um projeto só, escolha o projeto.' })}
                select label="Esta NF é de qual projeto?"
                slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              >
                <MenuItem value="">Contrato inteiro (sem projeto)</MenuItem>
                {(projeto.projetos || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
              </TextField>
            </Box>
          </Secao>
        )}

        <Secao titulo="Competência">
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 3' } }}>
            <TextField {...campo('mes')} select label="Mês" onChange={(e) => setForm((f) => ({ ...f, mes: Number(e.target.value) }))}>
              {MESES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 3' } }}>
            <TextField {...campo('ano')} label="Ano" type="number" onChange={(e) => setForm((f) => ({ ...f, ano: Number(e.target.value) }))} />
          </Box>
        </Secao>

        <Secao titulo="Nota fiscal">
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 2' } }}>
            <TextField {...campo('numero_nf')} label="Número da NF" />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 2' } }}>
            <TextField
              {...campo('valor')} label="Valor da NF" type="number"
              slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 1' } }}>
            <TextField {...campo('numero_parcela')} label="Parcela" type="number" slotProps={{ htmlInput: { min: 1 } }} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 1' } }}>
            <TextField {...campo('total_parcelas')} label="de" type="number" slotProps={{ htmlInput: { min: 1 } }} />
          </Box>

          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 6' } }}>
            <input
              ref={inputArquivo} type="file" hidden accept={EXTENSOES.join(',')}
              onChange={(e) => { escolherArquivo(e.target.files); e.target.value = ''; }}
            />
            <Box
              onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
              onDragLeave={() => setArrastando(false)}
              onDrop={(e) => { e.preventDefault(); setArrastando(false); escolherArquivo(e.dataTransfer.files); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', p: 1.5, borderRadius: 2,
                border: `1.5px dashed ${erros.arquivo_nf ? STATUS.em_aberto.cor : arrastando ? COR.destaque : '#cbd5e1'}`,
                backgroundColor: arrastando ? '#fff5f7' : COR.fundo, transition: 'background-color 150ms ease',
              }}
            >
              {arquivo || temArquivoAtual ? (
                <>
                  <InsertDriveFileOutlinedIcon sx={{ color: COR.navy }} />
                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: COR.ink, wordBreak: 'break-all' }}>
                      {arquivo ? arquivo.name : receita.arquivo_nf_nome}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: COR.texto2 }}>
                      {arquivo ? `${formatarTamanho(arquivo.size)}, será anexado ao salvar` : 'NF já anexada a este lançamento'}
                    </Typography>
                  </Box>
                  {temArquivoAtual && (
                    <Button size="small" onClick={() => abrirNotaFiscal(receita).catch((e) => setMensagem(e.message))}>Ver NF</Button>
                  )}
                  <Button size="small" onClick={() => inputArquivo.current?.click()}>Trocar</Button>
                  <Button
                    size="small" color="error"
                    onClick={() => { setArquivo(null); if (temArquivoAtual) setRemoverArquivo(true); }}
                  >
                    Remover
                  </Button>
                </>
              ) : (
                <>
                  <CloudUploadOutlinedIcon sx={{ color: COR.texto2 }} />
                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: COR.ink }}>
                      {removerArquivo ? 'A NF anexada será removida ao salvar' : 'Anexe a NF deste mês'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: COR.texto2 }}>
                      Arraste o arquivo aqui ou escolha no computador. PDF, XML, JPG ou PNG, até 10 MB.
                    </Typography>
                  </Box>
                  {removerArquivo && <Button size="small" onClick={() => setRemoverArquivo(false)}>Desfazer</Button>}
                  <Button size="small" variant="outlined" onClick={() => inputArquivo.current?.click()}>Escolher arquivo</Button>
                </>
              )}
            </Box>
            {erros.arquivo_nf && (
              <Typography role="alert" sx={{ fontSize: 12.5, mt: 0.75, fontWeight: 600, color: STATUS.em_aberto.texto }}>
                {erros.arquivo_nf}
              </Typography>
            )}
          </Box>
        </Secao>

        <Secao titulo="Vencimento e recebimento">
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 2' } }}>
            <TextField
              {...campo('data_vencimento')} label="Data de vencimento" type="date"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 2' } }}>
            <TextField
              {...campo('data_recebimento', { ajuda: !form.data_recebimento ? 'Deixe em branco enquanto não receber.' : undefined })}
              label="Data do recebimento" type="date"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            {!form.data_recebimento && (
              <Button size="small" sx={{ mt: 0.5, px: 0.5 }} onClick={() => setForm((f) => ({ ...f, data_recebimento: hojeISO() }))}>
                Recebida hoje
              </Button>
            )}
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 2' } }}>
            <TextField
              {...campo('valor_liquido_recebido', { ajuda: 'Valor que entrou na conta, já sem retenções.' })}
              label="Valor líquido recebido" type="number"
              slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
            />
            {form.valor_liquido_recebido === '' && Number(form.valor) > 0 && (efetivo === 'recebido' || form.data_recebimento) && (
              <Button size="small" sx={{ mt: 0.5, px: 0.5 }} onClick={() => setForm((f) => ({ ...f, valor_liquido_recebido: f.valor }))}>
                Usar o valor da NF
              </Button>
            )}
          </Box>
        </Secao>

        <Secao titulo="Situação">
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 3' }, display: 'flex', flexDirection: 'column', gap: 0.75, justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <StatusChip status={efetivo} corrigido={Boolean(form.status_manual)} automatico={automatico} />
              <Typography sx={{ fontSize: 13, color: COR.texto2 }}>{prazo}</Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: COR.texto2 }}>
              {form.status_manual
                ? `O sistema calcularia: ${STATUS[automatico].label}.`
                : 'Calculado sozinho pelo vencimento e pelo recebimento.'}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 3' } }}>
            <TextField
              {...campo('status_manual')} select label="Corrigir status manualmente"
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">Automático (recomendado)</MenuItem>
              {STATUS_ORDEM.map((s) => <MenuItem key={s} value={s}>{STATUS[s].label}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', sm: 'span 6' } }}>
            <TextField {...campo('observacao')} label="Observação (opcional)" multiline minRows={2} />
          </Box>
        </Secao>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${COR.borda}` }}>
        <Button onClick={onFechar} disabled={salvando} sx={{ color: COR.texto2 }}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={salvando} sx={{ backgroundColor: COR.navy, '&:hover': { backgroundColor: COR.ink } }}>
          {salvando ? 'Salvando...' : 'Salvar lançamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
